import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Footer from "../../Components/Footer/Footer";
import Header from "../Header/Header";
import { Sofa, Building2, Sparkles, ShieldCheck } from "lucide-react";

const AboutUs = () => {
	const containerRef = useRef(null);

	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start start", "end start"],
	});

	const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
	const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

	return (
		<>
			<Header />

			<div ref={containerRef} className="bg-gray-100">

				{/* HERO SECTION */}
				<div className="relative h-[60vh] overflow-hidden">
					<motion.img
						src="/assets/about-us.jpg"
						alt="About Zakkuu"
						style={{ y: imageY }}
						className="absolute inset-0 w-full h-full object-cover"
					/>

					<div className="absolute inset-0 bg-black/40 flex items-center justify-center">
						<motion.h1
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8 }}
							className="text-white text-4xl md:text-5xl font-bold"
						>
							About Zakkuu
						</motion.h1>
					</div>
				</div>

				{/* CONTENT */}
				<motion.div
					style={{ y: contentY }}
					className="relative z-10 -mt-24 px-4 pb-20"
				>
					<div className="bg-white max-w-6xl mx-auto shadow-xl p-8 md:p-12 grid gap-10">

						{/* LEFT CONTENT */}
						<motion.div
							initial={{ opacity: 0, x: -40 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6 }}
							viewport={{ once: true }}
							className="space-y-8"
						>
							<h2 className="text-3xl font-bold">About Zakkuu</h2>

							{/* INTRO */}
							<div>
								<p className="text-gray-600 leading-relaxed text-justify">
									Zakkuu is a premium furniture brand dedicated to transforming homes and workplaces with thoughtfully designed, high-quality furniture. At Zakkuu, we believe that furniture is not just a functional necessity but an essential part of creating a comfortable, inspiring, and beautiful environment. Our mission is to provide furniture that combines elegance, durability, and modern design to elevate the spaces where people live, work, and relax.
								</p>
							</div>

							{/* MISSION */}
							<div>
								<h3 className="text-xl font-semibold mb-2">Our Mission</h3>
								<p className="text-gray-600 leading-relaxed text-justify">
									Our mission is to provide furniture that combines elegance, durability, and modern design to elevate the spaces where people live, work, and relax.
								</p>
							</div>

							{/* CRAFTSMANSHIP */}
							<div>
								<h3 className="text-xl font-semibold mb-2">Craftsmanship & Quality</h3>
								<p className="text-gray-600 leading-relaxed text-justify">
									Founded with a passion for quality craftsmanship and contemporary aesthetics, Zakkuu offers a wide range of furniture solutions for every corner of your home and office. From stylish living room furniture and comfortable bedroom sets to functional office desks and storage solutions, each product is carefully selected and designed to deliver both style and practicality.
								</p>

								<p className="text-gray-600 leading-relaxed text-justify mt-2">
									We focus on using high-quality materials and expert craftsmanship to ensure that every piece of furniture stands the test of time.
								</p>
							</div>

							{/* DESIGN PHILOSOPHY */}
							<div>
								<h3 className="text-xl font-semibold mb-2">Design Philosophy</h3>
								<p className="text-gray-600 leading-relaxed text-justify">
									At Zakkuu, we understand that every home and workspace is unique. That is why our furniture collections are designed to suit a variety of interior styles, whether you prefer modern minimalism, timeless classics, or elegant contemporary designs.
								</p>

								<p className="text-gray-600 leading-relaxed text-justify mt-2">
									Our goal is to help customers create spaces that reflect their personality, enhance comfort, and improve everyday living.
								</p>
							</div>

							{/* CUSTOMER EXPERIENCE */}
							<div>
								<h3 className="text-xl font-semibold mb-2">Customer First</h3>
								<p className="text-gray-600 leading-relaxed text-justify">
									Customer satisfaction is at the heart of everything we do. From product design to delivery and support, we are committed to providing a seamless and enjoyable shopping experience. Our team continuously works to ensure that every Zakkuu product meets the highest standards of quality, functionality, and design.
								</p>
							</div>

							{/* FINAL STATEMENT */}
							<div>
								<h3 className="text-xl font-semibold mb-2">The Zakkuu Promise</h3>
								<p className="text-gray-600 leading-relaxed text-justify">
									Zakkuu is more than just a furniture store—it is a place where design meets comfort and quality meets innovation. We strive to bring together beautiful craftsmanship, modern trends, and reliable durability to create furniture that truly enhances your lifestyle.
								</p>
								<p className="text-gray-600 leading-relaxed text-justify">
									Whether you are furnishing a new home, upgrading your office, or looking for the perfect piece to complete your space, Zakkuu is here to help you find furniture that combines beauty, comfort, and functionality.
								</p>

								<p className="text-gray-700 font-medium mt-3">
									Discover the art of living better with Zakkuu — your trusted destination for premium furniture for homes and offices.
								</p>
							</div>
						</motion.div>
					</div>
				</motion.div>
			</div>

			<Footer />
		</>
	);
};

export default AboutUs;